/**
 * HeroAvatar Component
 *
 * Renders hero character avatars with consistent sizing and styling.
 */

import { COPILOT_AVATAR, getHeroAvatarAsset, HERO_AVATAR_SIZES } from '../images/heroes';
import type { HeroType, ImageSize } from '../types';

/**
 * Props for HeroAvatar component
 */
export interface HeroAvatarProps {
  /** The hero to display (or 'copilot' for the AI assistant) */
  hero: HeroType | 'copilot';
  /** Size preset */
  size?: ImageSize;
  /** Custom size in pixels (overrides size preset) */
  customSize?: number;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show a drop shadow effect */
  withShadow?: boolean;
  /** Optional click handler */
  onClick?: () => void;
}

/**
 * HeroAvatar Component
 *
 * Renders hero character images with consistent sizing.
 *
 * @example
 * ```tsx
 * <HeroAvatar hero="checkup" size="lg" />
 * <HeroAvatar hero="teleportal" size="xl" withShadow />
 * <HeroAvatar hero="copilot" size="md" />
 * ```
 */
export function HeroAvatar({
  hero,
  size = 'md',
  customSize,
  className = '',
  withShadow = false,
  onClick,
}: HeroAvatarProps) {
  // Get asset info
  const asset = hero === 'copilot' ? COPILOT_AVATAR : getHeroAvatarAsset(hero);

  // Calculate size
  const pixelSize = customSize || HERO_AVATAR_SIZES[size];

  // Build class name
  const combinedClassName = [
    'object-contain',
    withShadow ? 'drop-shadow-xl' : '',
    onClick ? 'cursor-pointer' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <img
      src={asset.path}
      alt={asset.alt}
      width={pixelSize}
      height={pixelSize}
      className={combinedClassName}
      onClick={onClick}
      loading="lazy"
      decoding="async"
    />
  );
}

export default HeroAvatar;
